import { useEffect, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { getArticleBySlug, getRelatedArticles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  const article = useMemo(() => getArticleBySlug(slug || ''), [slug]);
  const relatedArticles = useMemo(() => getRelatedArticles(slug || ''), [slug]);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-20 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
              <i className="ri-file-search-line text-2xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-bold text-foreground-900">文章未找到</h1>
            <p className="mt-2 text-foreground-500">该文章可能已被移除或链接有误</p>
            <Link
              to="/"
              className="inline-block mt-6 px-6 py-3 bg-primary-500 text-white font-medium rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.image,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    dateModified: article.date,
    publisher: {
      '@type': 'Organization',
      name: 'Po文导航',
    },
  };

  return (
    <>
      <SEOHead
        title={`${article.title} - Po文导航`}
        description={article.excerpt}
        keywords={`po文网站,${article.tags.join(',')},创作者平台`}
        canonicalUrl={`https://powen.example.com/article/${article.slug}`}
        ogType="article"
        ogImage={article.image}
        articlePublishedTime={article.date}
        articleModifiedTime={article.date}
        schema={articleSchema}
      />
      <Navbar />
      <main className="pt-20">
        <article className="max-w-4xl mx-auto px-4 md:px-6 py-8">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: article.categoryName, path: `/category/${article.category}` },
              { label: article.title },
            ]}
          />

          <div className="mt-8">
            <div className="flex flex-wrap items-center gap-2 mb-4">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="text-xs px-3 py-1 rounded-full bg-accent-100 text-accent-700 font-medium"
                >
                  {tag}
                </span>
              ))}
              <span className="text-xs text-foreground-400">·</span>
              <span className="text-xs text-foreground-400">{article.readTime}</span>
            </div>

            <h1 className="text-2xl md:text-4xl font-bold font-heading text-foreground-900 leading-tight">
              {article.title}
            </h1>

            <div className="mt-6 flex items-center gap-3">
              <img
                src={article.authorAvatar}
                alt={article.author}
                className="w-10 h-10 rounded-full object-cover"
              />
              <div>
                <p className="text-sm font-semibold text-foreground-800">{article.author}</p>
                <p className="text-xs text-foreground-400">
                  {article.date} · {article.viewCount.toLocaleString()} 次阅读
                </p>
              </div>
            </div>
          </div>

          <div className="mt-8 aspect-[16/9] rounded-xl overflow-hidden">
            <img
              src={article.image}
              alt={article.title}
              className="w-full h-full object-cover"
            />
          </div>

          <div
            className="mt-10 prose prose-sm md:prose-base max-w-none
              prose-headings:font-heading prose-headings:text-foreground-900 prose-headings:font-bold
              prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:my-4
              prose-a:text-primary-600 prose-a:no-underline hover:prose-a:underline
              prose-strong:text-foreground-900 prose-strong:font-semibold
              prose-li:text-foreground-700 prose-li:my-1
              prose-h2:text-xl md:prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4
              prose-h3:text-lg md:prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="mt-12 pt-8 border-t border-background-200">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-foreground-500">标签：</span>
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="text-xs px-3 py-1 rounded-full bg-secondary-100 text-secondary-700"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="bg-background-100 py-14">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <h2 className="text-xl font-bold font-heading text-foreground-900 mb-6">
                相关文章
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.slug}`}
                    className="group bg-background-50 rounded-xl overflow-hidden border border-background-200 hover:border-primary-300 transition-all duration-300 cursor-pointer"
                  >
                    <div className="aspect-[16/10] overflow-hidden">
                      <img
                        src={ra.image}
                        alt={ra.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2">
                        {ra.title}
                      </h3>
                      <p className="mt-1 text-xs text-foreground-400">
                        {ra.date} · {ra.readTime}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}